/* Production Notifications — full-page view of the notification feed.
 * Mirrors the in-header bell dropdown but with permanent screen real
 * estate so the tech can leave it open while working through the queue.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
    Bell, Package, ArrowUUpLeft, CheckCircle, Fire, ArrowRight,
} from "@phosphor-icons/react";


const KIND_META = {
    new_assignment: { label: "New cases to acknowledge", Icon: Package,        chipClass: "bg-violet-100 text-violet-700" },
    rework:         { label: "Sent back for rework",     Icon: ArrowUUpLeft,    chipClass: "bg-amber-100 text-amber-800" },
    flag_acked:     { label: "Your flags were cleared",  Icon: CheckCircle,     chipClass: "bg-emerald-100 text-emerald-700" },
    due_date_alert: { label: "Past SLA — STAT/Rush",     Icon: Fire,            chipClass: "bg-rose-100 text-rose-700" },
};

const PRIORITY_BADGE = {
    stat: { label: "STAT", cls: "bg-rose-600 text-white" },
    rush: { label: "Rush", cls: "bg-amber-500 text-white" },
};


export default function ProductionNotifications() {
    const [data, setData] = useState({ total: 0, items: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/production/notifications")
            .then((r) => setData(r.data || { total: 0, items: [] }))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-5 min-w-0" data-testid="production-notifications-page">
            <header>
                <h1 className="font-display text-3xl font-bold tracking-tight">Notifications</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    {loading
                        ? "Loading…"
                        : data.total === 0
                            ? "You're all caught up."
                            : `${data.total} item${data.total === 1 ? "" : "s"} on your plate.`}
                </p>
            </header>

            {!loading && data.items.length === 0 && (
                <div className="rounded-2xl bg-card border border-border p-10 text-center">
                    <CheckCircle size={32} weight="fill" className="mx-auto text-emerald-500 mb-3" />
                    <div className="text-sm font-semibold">You're all caught up</div>
                    <div className="text-xs text-muted-foreground mt-1">
                        New assignments, rework returns, and SLA alerts will appear here.
                    </div>
                </div>
            )}

            {!loading && data.items.length > 0 && (
                <div className="space-y-2.5">
                    {data.items.map((it) => {
                        const meta = KIND_META[it.kind] || { label: it.kind, Icon: Bell, chipClass: "bg-slate-100" };
                        const pBadge = PRIORITY_BADGE[it.priority];
                        return (
                            <Link
                                key={it.id}
                                to={`/coe/production/case/${it.case_id}`}
                                className="flex items-start gap-3 bg-card border border-border rounded-xl p-3 hover:shadow-sm hover:-translate-y-px transition-all"
                                data-testid={`production-notif-page-row-${it.id}`}
                            >
                                <div className={`w-9 h-9 rounded-lg ${meta.chipClass} flex items-center justify-center shrink-0`}>
                                    <meta.Icon size={15} weight="fill" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-sm font-semibold">{meta.label}</span>
                                        <span className="text-xs text-muted-foreground">·</span>
                                        <span className="text-sm truncate">{it.patient_name}</span>
                                        {it.case_code && (
                                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{it.case_code}</span>
                                        )}
                                        {pBadge && (
                                            <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${pBadge.cls}`}>
                                                {pBadge.label}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1 leading-snug">{it.message}</div>
                                </div>
                                <ArrowRight size={14} className="text-muted-foreground shrink-0 mt-1" />
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
